import { omsApi } from 'oddle-shop-monorepo.services/oms/base-api'

export const injectedOmsApi = omsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMenu: builder.query<
      Oms.ApiResponse<{ tags: Oms.MenuCategory[] }>,
      {
        menuId: string
        storeOrderId: string
        mode: string
      }
    >({
      query: ({ menuId, storeOrderId, mode }) => ({
        url: '/cart-api-v3/ajax-retrieve-menu',
        params: {
          'menu-id': menuId,
          mode,
          ...(storeOrderId
            ? {
                'store-order-id': storeOrderId,
              }
            : {}),
        },
      }),
      providesTags: ['MenuItems'],
    }),
    getFeatureToggles: builder.query<
      Oms.V4ApiResponse<Oms.MenuFeatureToggles>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/api/v4/menus/${menuId}/features`,
        params: {
          'features[]': [
            'enableNewCheckoutFlow',
            'enableUniversalPromotion',
            'showDeliveryFulfilled',
            'enableCustomerSaveCreditCardOnStripe',
            'enableNewStructureForShopEditor',
            'newOddlePassFlow',
            'enableNewThankYouPage',
            'enableChatSupportByCountry',
            'disableLateDeliveryNotification',
            'enableOptionsForInvoiceInstructions',
            'enableLimitAdvancedOrders',
            'enableStripePaymentElement',
            'enableAccountLevelMarketingSubscription',
            'enableTimeLimitUnavailability',
            'enableNewPromotionFilmStripOnShop',
          ],
        },
      }),
    }),
    getStoreItemAvailability: builder.query<
      Oms.V4ApiResponse<Oms.StoreItemAvailability[]>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/api/v4/unavailableResources/menus/${menuId}`,
      }),
    }),
    getAllStores: builder.query<
      Oms.ApiResponse<{ stores: Oms.Store[] }>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/cart-api-v3/ajax-retrieve-all-stores?menu-id=${menuId}`,
      }),
    }),
    getPickupStores: builder.query<
      Oms.ApiResponse<{ stores: Oms.Store[] }>,
      {
        storeOrderId?: string
        storeId?: string
      }
    >({
      query: ({ storeOrderId, storeId }) => ({
        url: `/cart-api-v3/ajax-retrieve-stores`,
        params: {
          'store-order-id': storeOrderId,
          'store-id': storeId,
        },
      }),
    }),
    getEligiblePromotions: builder.query<
      Oms.ApiResponse<Oms.EligiblePromotions>,
      string | undefined
    >({
      query: (storeOrderId) => ({
        url: `/cart-api-v3/ajax-retrieve-eligible-promotions`,
        params: {
          'store-order-id': storeOrderId,
        },
      }),
    }),
    getTimeSlots: builder.query<
      Oms.ApiResponse<{ timeIntervals: Oms.AvailableTimeSlots[] }>,
      {
        storeOrderId?: string
        date?: string
        storeId?: string
      }
    >({
      query: ({ storeOrderId, date }) => ({
        url: '/api/v4/cart/time-intervals',
        params: {
          'store-order-id': storeOrderId,
          'selected-date': date,
        },
      }),
      // Slots must always be fetched fresh: availability changes server-side,
      // and the redux mirror (order.availableTimeSlots) only updates via
      // matchPending/matchFulfilled — a cache hit fires neither, leaving the
      // mirror stale. Force a network request on every subscribe/trigger and
      // drop the cache entry as soon as the last subscriber unmounts.
      forceRefetch: () => true,
      keepUnusedDataFor: 0,
    }),
    getTrackers: builder.query<{ data: Oms.Tracker[] }, string | undefined>({
      query: (menuId) => ({
        url: `/cart-api-v3/menus/${menuId}/analytics-tracker`,
      }),
    }),
    getScripts: builder.query<Oms.ApiResponse<Oms.Script>, string | undefined>({
      query: (menuId) => ({
        url: `/cart-api-v3/ajax-get-shopping-cart-trackers?menu-id=${menuId}`,
      }),
    }),
    getTermsAndConditions: builder.query<
      Oms.V4ApiResponse<string>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/api/v4/cart/${menuId}/terms-and-conditions`,
      }),
    }),
    getPrivacyPolicy: builder.query<
      Oms.V4ApiResponse<string>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/api/v4/cart/${menuId}/privacy-policy`,
      }),
    }),
  }),
})

export { omsApi }

export const {
  useGetMenuQuery,
  useLazyGetMenuQuery,
  useGetFeatureTogglesQuery,
  useGetStoreItemAvailabilityQuery,
  useGetAllStoresQuery,
  useGetPickupStoresQuery,
  useLazyGetEligiblePromotionsQuery,
  useGetEligiblePromotionsQuery,
  useGetTimeSlotsQuery,
  useLazyGetTimeSlotsQuery,
  useGetTrackersQuery,
  useGetScriptsQuery,
  useGetTermsAndConditionsQuery,
  useGetPrivacyPolicyQuery,
} = injectedOmsApi
